import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useContext,
  useState,
  type ReactNode,
} from 'react'
import { QueryClientContext } from '@tanstack/react-query'
import type { components } from '../api/generated/schema'
import { setTokenProvider } from '../api/client'
import {
  clearSessionRevoked,
  getSessionRevoked,
  subscribeSessionRevoked,
} from '../api/sessionRevokedStore'
import { forgetReturnTo } from './returnTo'
import { markOnboarded as patchOnboarded } from '../api/onboarding'
import { emit, setAnalyticsUser } from '../platform/analytics/analytics'
import {
  INITIAL_AUTH_STATE,
  holdsUser,
  reduce,
  type AuthEvent,
  type AuthState,
} from './authMachine'
import { analyticsCode, authErrorFor, type FieldError } from './errors'
import {
  AuthFailure,
  MfaRequiredError,
  type AuthPort,
  type AuthUser,
  type SocialSignIn,
  type EnrolledFactor,
  type AccountIdentity,
  type TotpEnrolment,
  type PhoneEnrolment,
} from './port'

type Profile = components['schemas']['UserResponse']

export type { AuthState } from './authMachine'

/**
 * What a sign-in attempt actually did.
 *
 * Returned rather than inferred, because no caller can work it out.
 *
 *   `complete`     a session exists in THIS document. Measurable by the form.
 *   `mfaRequired`  a second factor was raised. `signIn` swallows
 *                  `MfaRequiredError` to dispatch the challenge, so it resolves
 *                  identically to success — and a form reading `auth.status`
 *                  afterwards reads its own render closure, from before the
 *                  dispatch. That made `signin_completed` fire with
 *                  `mfa_used: false` for a user sitting on a challenge screen.
 *   `redirecting`  the document is about to be replaced. Nothing has happened
 *                  yet, and nothing that happens next happens here.
 *
 * The third arm is the one that reads like a technicality and is not.
 * `signInWithGoogle('redirect')` resolves only because the navigation has not
 * landed, so calling that `complete` had a single tap emit `signin_completed`
 * and then, on return, `signin_failed` — a completion rate above 100% — while a
 * redirect that actually SUCCEEDED emitted nothing at all. Completion for that
 * path is emitted where the session genuinely arrives: `completeRedirect`.
 */
export type SignInOutcome = 'complete' | 'mfaRequired' | 'redirecting'

export type AuthActions = {
  signIn(email: string, password: string): Promise<SignInOutcome>
  signUp(email: string, password: string): Promise<void>
  signInWithGoogle(mode: 'popup' | 'redirect'): Promise<SignInOutcome>
  /**
   * `keepReturnTo: false` for a sign-out the user chose from a page they should not
   * come back to (sign out everywhere): `RequireAuth` then stores no return path.
   */
  signOut(options?: { keepReturnTo?: boolean }): Promise<void>
  sendPasswordReset(email: string): Promise<void>
  /**
   * The other half of the reset, and the half that was missing.
   *
   * `sendPasswordReset` above puts a Firebase `oobCode` in the user's inbox and
   * the app had nothing that could spend one — the flow ended at the email.
   * These two close it: `verifyResetCode` answers whether a link is still good
   * and whose account it is, and `completeReset` spends it.
   *
   * Two calls rather than one, because the screen has to know the answer to the
   * first before the form is worth drawing. An expired link discovered only on
   * submit means the user chooses a password, confirms it, and is then told the
   * link died — after the work, instead of before it.
   *
   * `verifyResetCode` resolves to the EMAIL the code belongs to. That is what
   * lets the screen say whose password is being changed, and what lets
   * "Save and sign in" sign the user in afterwards without asking them to type
   * an address they have already proved they control.
   */
  verifyResetCode(oobCode: string): Promise<string>
  completeReset(oobCode: string, newPassword: string): Promise<void>
  sendVerificationEmail(): Promise<void>
  retrySession(): Promise<void>
  /**
   * Record that first run is done, and stop the guard sending them back.
   *
   * **The state update is not conditional on the write.** `RequireAuth` reads
   * `profile.is_onboarded`, and that profile is written only by `bootstrap`,
   * which re-runs on a uid change and nothing else — so a PATCH alone left the
   * flag `false` for the life of the tab, the guard bounced the user straight
   * back to first run, and the screen asked again. web-architect found that;
   * it is the second redirect trap in this codebase and both had the same
   * shape, a guard whose exit condition nothing could actually satisfy.
   */
  markOnboarded(): Promise<void>
  /**
   * Enrolment, passed through unchanged.
   *
   * No provider logic sits on these: they neither start nor end a session, and
   * the auth STATE is unaffected by adding a factor. Exposed here only so a
   * screen reaches them the same way it reaches everything else, rather than
   * importing the port and bypassing the context the app is built around.
   */
  enrolledFactors(): EnrolledFactor[]
  identity(): AccountIdentity
  beginTotpEnrolment(): Promise<TotpEnrolment>
  beginPhoneEnrolment(phoneNumber: string, container: HTMLElement): Promise<PhoneEnrolment>
  removeFactor(factorId: string): Promise<void>
  reauthMethod(): 'password' | 'google' | null
  reauthenticate(password?: string): Promise<void>
  /** Drop a redirect failure, so a fresh attempt does not carry a stale message. */
  clearRedirectError(): void
}

/**
 * `redirectError` sits beside the state union rather than inside it.
 *
 * A returning redirect that failed leaves the SESSION exactly where it was —
 * `signedOut` — so there is no new state to be in. The failure is a fact about
 * the last attempt, and a seventh arm would force every consumer of the union
 * to handle a case that is not about the session at all.
 */
export type AuthContextValue = AuthState &
  AuthActions & {
    redirectError: FieldError | null
    /** True after a sign-out asked for `keepReturnTo: false`, until a session is established. */
    noReturnTo?: boolean
  }

export const AuthContext = createContext<AuthContextValue | null>(null)

type Props = {
  port: AuthPort
  /** Injected rather than imported, so this module is testable without the API. */
  startSession: () => Promise<Profile>
  children: ReactNode
}

/**
 * The ambient query client. `QueryProvider` is always above this component in the app, and
 * the cache clear on sign-out (#781) depends on it: a missing provider must fail loudly,
 * not turn every `clear()` into a silent no-op. Only under vitest (`MODE === 'test'`),
 * where most auth tests mount without a cache, is `undefined` tolerated.
 */
function useQueryClientForSignOut() {
  const client = useContext(QueryClientContext)
  if (client === undefined && import.meta.env.MODE !== 'test') {
    throw new Error('AuthProvider must be mounted inside a QueryClientProvider')
  }
  return client
}

export function AuthProvider({ port, startSession, children }: Props) {
  const queryClient = useQueryClientForSignOut()
  const [state, rawDispatch] = useReducer(reduce, INITIAL_AUTH_STATE)

  // The redirect's own failure. See `AuthContextValue` for why it is not a
  // state arm.
  const [redirectError, setRedirectError] = useState<FieldError | null>(null)
  const [noReturnTo, setNoReturnTo] = useState(false)

  // A ref, not state: the bootstrap closure needs the current user without
  // re-subscribing to the port every time the user object changes identity.
  const userRef = useRef<AuthUser | null>(null)

  /**
   * Which bootstrap is allowed to land — web-tester's Defect 1.
   *
   * `startSession()` is an in-flight request and every `setState` after it used
   * to be unconditional, so anything that happened while it was outstanding got
   * overwritten when it resolved. Sign out mid-bootstrap and the app went back
   * to `signedIn` with the pre-sign-out profile, the signed-out user's hash
   * restored in analytics, and the token provider NOT reinstalled — measured as
   * `Authorization: null` on the next call, so every request 401s while the UI
   * shows a healthy session. Two users reported in quick succession produced
   * the same shape: whichever session POST resolved last won, so user B could
   * be shown user A's profile.
   *
   * Bumped by anything that supersedes a bootstrap — a new user, a sign-out.
   * A bootstrap whose ticket is stale drops its result instead of applying it.
   */
  const generation = useRef(0)

  /**
   * Set when the user deliberately signs out, and never by anything else.
   *
   * `challenged` is open from `signedOut` — it has to be, since that is exactly
   * where a legitimate redirect challenge arrives — but `signedOut` does not
   * record WHY it is signed out. So a `getRedirectResult` still pending across a
   * full sign-in and sign-out came back with a second-factor prompt for someone
   * who had just asked to leave, and its `submit` would sign the account back
   * in. web-architect reproduced it after the generation ticket came out.
   *
   * A ref rather than a seventh state arm or a counter: the rule is "an explicit
   * sign-out cancels a pending redirect", which is about intent, not about
   * timing. A generation counter also counts token refreshes, which is what made
   * it the wrong instrument here in the first place.
   */
  const redirectAbandoned = useRef(false)

  /**
   * Mirrors `state` for the listener, which is not re-created per render and so
   * would otherwise close over the state at subscribe time.
   *
   * Kept in step by running `reduce` here as well as letting React run it. That
   * is a deliberate double call, not an oversight: `useReducer` gives no
   * synchronous read of the resulting state, and the listener has to decide
   * whether to start a session POST against the state as of that moment — a
   * decision one tick stale is the bug class this whole file has been fixing.
   * `reduce` is pure, so calling it twice is free, and every dispatch goes
   * through this wrapper, so the ref can never drift from the state.
   */
  const stateRef = useRef<AuthState>(INITIAL_AUTH_STATE)
  const dispatch = useCallback((event: AuthEvent) => {
    stateRef.current = reduce(stateRef.current, event)
    rawDispatch(event)
  }, [])

  /**
   * Which completion event a social sign-in is, decided in ONE place.
   *
   * Google sign-up and Google sign-in are a single Firebase operation, so the
   * SCREENS cannot tell them apart — `/signup` reported `signup_completed` for
   * returning users and `/signin` reported `signin_completed` for brand-new
   * ones, and the redirect path could report nothing at all because the
   * document that started it is gone. #172.
   *
   * The provider owns this because it is the only thing present for both arms:
   * the popup resolves here through `signInWithGoogle`, and the redirect
   * arrives here through `completeRedirect` on a later document. The screens
   * keep the EMAIL events, where the operation itself says which it was.
   */
  const reportSocial = useCallback((outcome: SocialSignIn) => {
    if (outcome.isNewUser) {
      emit('signup_completed', { method: 'google' })
      return
    }
    // `mfa_used: false` — a challenged sign-in never reaches here, because the
    // port throws `MfaRequiredError` instead of resolving. `MfaChallenge`
    // emits the `true` half when the factor clears.
    emit('signin_completed', { method: 'google', mfa_used: false })
  }, [])

  const bootstrap = useCallback(
    async (user: AuthUser) => {
      const ticket = (generation.current += 1)
      dispatch({ type: 'reported', user })
      try {
        const profile = await startSession()
        if (generation.current !== ticket) return
        // `userRef.current`, not the `user` this closure was called with. A
        // refresh arriving mid-POST updates the `bootstrapping` arm in the
        // listener, and this line would then overwrite it with the object
        // captured before the POST started — same generation, so the ticket
        // does not stop it, and `emailVerified` silently reverts. Safe because
        // a DIFFERENT uid and a sign-out both bump the generation, so anything
        // reaching here is the same user, only fresher. web-architect found it.
        dispatch({ type: 'established', user: userRef.current ?? user, profile })
        // A new session exists, so the revocation that ended the last one is
        // history: release the hold (and the sign-in page's message).
        clearSessionRevoked()
        setNoReturnTo(false)
        // The HASH of the id, never the id — see analytics.ts. Fire-and-forget
        // because identifying the user must not delay rendering the app.
        void setAnalyticsUser(profile.id)
      } catch {
        if (generation.current !== ticket) return
        // Swallowed on purpose — the STATE carries the failure, and the screen
        // that renders it is the recovery path. Rethrowing here would surface
        // as an unhandled rejection inside a listener nobody awaits.
        dispatch({ type: 'unavailable', user })
      }
    },
    [startSession, dispatch],
  )

  useEffect(() => {
    // `completeRedirect` first: a social sign-in returns to this page as a
    // fresh load, and the pending credential has to be consumed before the
    // listener can report anybody.
    //
    // The MFA branch is here as well as on `signIn`, and it is the one that
    // matters most: M1-R23 puts social sign-in on redirect below the
    // breakpoint, so this is the phone path. A bare `.catch(() => {})`
    // swallowed the challenge and left the app in `unknown` — web-tester's
    // Defect 3.
    // Two-argument `then`, not `.then().catch()`. The rejection handler must see
    // `completeRedirect`'s failure and nothing else — chaining a `.catch()`
    // after would also catch a throw from the fulfilment handler below, and
    // `emit()` throws in dev, so an analytics mistake would be rendered to the
    // user as a sign-in error.
    void port.completeRedirect().then((outcome) => {
      // The ONLY place a redirect sign-in can be measured. `signInWithGoogle`
      // returns `null` on redirect and the form emits nothing, because at that
      // point nothing has happened; here the session has genuinely arrived.
      // Guarded on a non-null outcome, or every reload of every route would
      // report a Google sign-in — the same over-count moved rather than fixed.
      //
      // Gated on `redirectAbandoned` for the same reason the MFA arm below is:
      // a sign-out landing between the redirect returning and this promise
      // resolving means the user asked to leave, and reporting a completed
      // sign-in for a session they abandoned is a measurement of something that
      // did not happen. Narrow, and the machinery was already here.
      if (outcome !== null && !redirectAbandoned.current) {
        reportSocial(outcome)
      }
    }, (error: unknown) => {
      if (error instanceof MfaRequiredError) {
        if (redirectAbandoned.current) return
        // NOT ticketed, deliberately, and it was for one commit. The concern is
        // real — a redirect losing the race to the listener must not replace an
        // established session — but a generation ticket is the wrong instrument
        // and an actively harmful one here: during an MFA challenge Firebase has
        // no signed-in user, so `onUserChanged(null)` fires FIRST and bumps the
        // generation, and the ticket then drops the challenge it exists to
        // deliver. The user lands on the sign-in screen with nothing. That is
        // the phone path, M1-R23.
        //
        // `authMachine` refuses `challenged` from `signedIn`, `bootstrapping`
        // and `sessionUnavailable` — every arm where Firebase has already
        // reported a user — which is the same protection stated as a rule about
        // states rather than a race about timing, and `signedOut` stays open
        // because that is precisely where a legitimate challenge arrives.
        dispatch({ type: 'challenged', challenge: error.challenge })
        return
      }
      // Everything else used to be dropped, which was the one gap this file
      // shipped knowing about: `auth/account-exists-with-different-credential`
      // and `auth/unauthorized-domain` both arrive here, both have curated
      // messages in KNOWN_CODES, and both reached the user as silence. The
      // sign-in form renders `redirectError` — see `AuthContextValue`.
      //
      // Nothing types this rejection, so an unmapped throw (a network failure
      // out of `getRedirectResult`) is given the unclassified message rather
      // than a field. `authErrorFor` refuses to guess a field for a code it
      // does not know, which is exactly right here.
      const detail = error instanceof AuthFailure ? error.detail : authErrorFor('redirect-failed')
      setRedirectError(detail)
      // Emitted HERE and not in the form. A form could only emit from an effect
      // keyed on a value it did not produce, and that effect re-fires on every
      // remount of the sign-in screen — one failure, many events.
      emit('signin_failed', { method: 'google', error_code: analyticsCode(detail.code) })
    })

    return port.onUserChanged((user) => {
      const previous = userRef.current
      userRef.current = user
      if (user === null) {
        // Supersedes any bootstrap still in flight.
        generation.current += 1
        setTokenProvider(async () => null)
        void setAnalyticsUser(null)
        // The cache belongs to the account that just left (#781).
        queryClient?.clear()
        // `reportedNone`, not `cleared` — the listener saw no user, which during
        // an outstanding MFA challenge is the state's definition rather than a
        // sign-out. `signOut()` below dispatches `cleared`.
        dispatch({ type: 'reportedNone' })
        return
      }
      setTokenProvider(() => port.getIdToken())
      // A direct switch A→B with no null between: the cache is A's (#781).
      if (previous !== null && previous.uid !== user.uid) queryClient?.clear()
      // The listener fires on token refresh too, not only on sign-in. Re-running
      // bootstrap there dropped the whole app out of `signedIn` into
      // `bootstrapping` — a loading flash on every panel — and POSTed a new
      // session on every refresh cycle for as long as the tab stayed open.
      //
      // Keyed on the uid we already have in hand, not on `signedIn` alone: a
      // refresh arriving DURING the bootstrap it would duplicate was the case
      // the narrower check missed, and it starts a second POST and a third
      // generation. `sessionUnavailable` is deliberately not in this set — a
      // fresh token is exactly the thing that might make the retry work.
      // The shared predicate, not a restatement of the rule and not an
      // inference from the table's return identity — see `holdsUser`. Both of
      // those were tried and both were wrong in the same way.
      if (holdsUser(stateRef.current, user.uid)) {
        // Skip the POST, but NOT the user object. The first version returned
        // here outright and so threw away what Firebase had just reported:
        // `emailVerified` could never flip from false to true for the life of
        // the tab, because a token refresh is exactly how that news arrives.
        // M1-R4's banner reads this field, so it would never clear without a
        // reload. `email` has the same problem. web-tester found it.
        dispatch({ type: 'refreshed', user })
        return
      }
      void bootstrap(user)
    })
  }, [port, bootstrap, dispatch, reportSocial, queryClient])

  const actions = useMemo<AuthActions>(
    () => ({
      async signIn(email, password) {
        try {
          await port.signIn(email, password)
          return 'complete'
        } catch (error) {
          if (error instanceof MfaRequiredError) {
            dispatch({ type: 'challenged', challenge: error.challenge })
            return 'mfaRequired'
          }
          // Rethrown so the FORM renders it on a field. A provider that stored
          // the message centrally would force every screen to render it the
          // same way, which is the toast M1-R2 rules out.
          throw error
        }
      },
      async signUp(email, password) {
        await port.signUp(email, password)
      },
      async signInWithGoogle(mode) {
        // Re-arms the redirect. `redirectAbandoned` is otherwise set once by
        // `signOut` and never cleared, so a genuinely new challenge on a second
        // `completeRedirect()` — which a parent re-render can cause, since the
        // effect's deps include `startSession` — was dropped because of a sign-out
        // several minutes earlier. Only this can arm a redirect, so it is the
        // only honest place to disarm the flag. web-architect reproduced the
        // stale-flag case; it is unreachable today because `getRedirectResult`
        // is one-shot per document, which is a property of a port that does not
        // exist yet and is asserted nowhere.
        redirectAbandoned.current = false
        try {
          const outcome = await port.signInWithGoogle(mode)
          // On `redirect` this resolves only because the navigation has not
          // happened yet; the document is replaced moments later and nothing
          // downstream of this line runs. Saying `complete` here was a sign-in
          // counted before it was attempted — see `SignInOutcome`.
          if (mode === 'redirect') return 'redirecting'
          // Reported HERE, not by the form. Only the port knows whether this
          // created the account, and both social arms have to answer the same
          // question the same way — see `reportSocial`.
          if (outcome !== null) reportSocial(outcome)
          return 'complete'
        } catch (error) {
          if (error instanceof MfaRequiredError) {
            dispatch({ type: 'challenged', challenge: error.challenge })
            return 'mfaRequired'
          }
          throw error
        }
      },
      async signOut(options) {
        if (options?.keepReturnTo === false) setNoReturnTo(true)
        // Local teardown is in `finally`: if the port's own sign-out rejects, the
        // user asked to leave and must not be left holding a live-looking session
        // with a cache from the account they tried to leave. The rejection still
        // propagates to the caller.
        try {
          await port.signOut()
        } finally {
          redirectAbandoned.current = true
          // Supersedes any bootstrap still in flight, so a session POST that
          // resolves after this cannot put the app back into `signedIn`.
          generation.current += 1
          setTokenProvider(async () => null)
          // Cleared here as well as in the listener above: `signOut` sets
          // `signedOut` itself rather than waiting to be told, and a fake port may
          // never report the null. Leaving the identity behind would attribute the
          // next user's events to this one.
          void setAnalyticsUser(null)
          // Server state belongs to the account that left (#781). Here and in the
          // listener are the only two ways a session ends, so no caller has to
          // remember it.
          queryClient?.clear()
          dispatch({ type: 'cleared' })
        }
      },
      async markOnboarded() {
        const current = stateRef.current
        if (current.status !== 'signedIn') return
        // Dispatched FIRST, and deliberately. The user is leaving now; whether
        // the server agrees is a separate question with a smaller consequence.
        dispatch({ type: 'profileUpdated', profile: { ...current.profile, is_onboarded: true } })
        // A ticket, exactly as `bootstrap` takes one. `profileUpdated` is a
        // no-op outside `signedIn`, so signing OUT is already safe — but it
        // does not check WHICH user, and signing out and back in as a second
        // account while this PATCH is outstanding would write account A's
        // profile onto account B's session. That is the failure the ticket at
        // line 209 exists for, and web-architect spotted it missing here.
        const ticket = generation.current
        const saved = await patchOnboarded()
        if (generation.current !== ticket) return
        // The server's own copy wins if it answers, so a field the backend
        // computes differently is not overwritten by our optimistic guess.
        if (saved !== null) dispatch({ type: 'profileUpdated', profile: saved })
      },
      sendPasswordReset: (email) => port.sendPasswordReset(email),
      // Straight passthroughs, like the line above. Neither touches the auth
      // STATE: `confirmPasswordReset` mints no session, so there is nothing to
      // dispatch and no listener to wait for. The screen signs in afterwards
      // through `signIn`, which does own the state transition.
      verifyResetCode: (oobCode) => port.verifyResetCode(oobCode),
      completeReset: (oobCode, newPassword) => port.completeReset(oobCode, newPassword),
      sendVerificationEmail: () => port.sendVerificationEmail(),
      enrolledFactors: () => port.enrolledFactors(),
      identity: () => port.identity(),
      beginTotpEnrolment: () => port.beginTotpEnrolment(),
      beginPhoneEnrolment: (phoneNumber, container) =>
        port.beginPhoneEnrolment(phoneNumber, container),
      removeFactor: (factorId) => port.removeFactor(factorId),
      reauthMethod: () => port.reauthMethod(),
      reauthenticate: (password) => port.reauthenticate(password),
      async retrySession() {
        // Guarded on the STATE, not on whether a user object happens to be
        // lying around. `signOut()` bumps the generation and applies
        // `signedOut`, but `userRef` is cleared only by the port listener —
        // which, as `signOut` itself notes, may never fire. Retrying off a
        // stale ref then rebuilt a `signedIn` state on top of a null token
        // provider: healthy UI, every request 401ing. web-architect found it.
        //
        // `sessionUnavailable` is the only arm a retry means anything in — it
        // is the one that renders a Retry control. From every other arm this is
        // a no-op rather than a transition nobody designed.
        if (stateRef.current.status !== 'sessionUnavailable') return
        const user = userRef.current
        if (user) await bootstrap(user)
      },
      clearRedirectError() {
        setRedirectError(null)
      },
    }),
    [port, bootstrap, dispatch, reportSocial, queryClient],
  )

  // The server revoked this session or device (a 401 the API client reports to the
  // store). Single-flight is the store's: a burst of 401s notifies once, so this
  // signs out once. The return path is dropped so re-sign-in lands on Overview.
  const { signOut } = actions
  useEffect(() => {
    return subscribeSessionRevoked(() => {
      if (getSessionRevoked() === null) return
      forgetReturnTo()
      if (stateRef.current.status === 'signedOut') return
      void signOut().catch(() => {
        // The port's own sign-out failed; the hold stays, and the guard's next
        // 401 cannot re-enter. Nothing more the user can act on here.
      })
    })
  }, [signOut])

  return (
    <AuthContext.Provider value={{ ...state, ...actions, redirectError, noReturnTo }}>
      {children}
    </AuthContext.Provider>
  )
}
